import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Users, CreditCard, Notebook, ClipboardText, ChartBar, Warning, UsersThree, MagnifyingGlass, Plus, CheckCircle, Trash, DownloadSimple, PaperPlaneTilt } from "@phosphor-icons/react";

const RED = "#D90429";
const GOLD = "#C9A961";

const DISC_LABEL = { kickboxing_muay_thai: "Kickboxing / Muay thaï", jiu_jitsu_can_ryu: "Jiu-jitsu Can Ryu", bjj: "BJJ" };

function Kpi({ icon: Icon, label, value, tone }) {
  const tones = { red: "text-[#D90429]", gold: "text-[#C9A961]", green: "text-emerald-400", amber: "text-amber-400", blue: "text-sky-400" };
  return (
    <div className="border border-white/10 bg-white/[0.02] p-5 rounded-lg" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 text-white/40 text-xs uppercase tracking-wider mb-2"><Icon size={16} className={tones[tone] || "text-white/40"} /> {label}</div>
      <div className={`text-3xl font-black ${tones[tone] || "text-white"}`}>{value}</div>
    </div>
  );
}

export default function AcademieKaizenModule() {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [dups, setDups] = useState([]);
  const [suivis, setSuivis] = useState([]);
  const [community, setCommunity] = useState(null);
  const [modLog, setModLog] = useState([]);
  const [cercleStats, setCercleStats] = useState(null);
  const [q, setQ] = useState("");
  const [newSuivi, setNewSuivi] = useState({ type: "relance", member_name: "", note: "", due_date: "" });

  const load = useCallback(() => {
    api.get("/academie-kaizen/overview").then((r) => setData(r.data)).catch(() => toast.error("Chargement échoué."));
    api.get("/academie-kaizen/duplicate-payments").then((r) => setDups(r.data.duplicates || [])).catch(() => {});
    api.get("/academie-kaizen/suivis").then((r) => setSuivis(r.data.suivis || [])).catch(() => {});
    api.get("/academie-kaizen/community").then((r) => setCommunity(r.data)).catch(() => {});
    api.get("/academie-kaizen/community/moderation-log").then((r) => setModLog(r.data.log || [])).catch(() => {});
    api.get("/academie-kaizen/community/stats").then((r) => setCercleStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addSuivi = async () => {
    if (!newSuivi.note.trim()) { toast.error("Ajoute une note."); return; }
    try {
      await api.post("/academie-kaizen/suivis", newSuivi);
      setNewSuivi({ type: "relance", member_name: "", note: "", due_date: "" });
      toast.success("Suivi créé.");
      load();
    } catch { toast.error("Échec."); }
  };
  const doneSuivi = async (id) => { await api.post(`/academie-kaizen/suivis/${id}/done`); load(); };
  const delSuivi = async (id) => { await api.delete(`/academie-kaizen/suivis/${id}`); load(); };
  const resolveDup = async (id) => { await api.post(`/academie-kaizen/duplicate-payments/${id}/resolve`); toast.success("Marqué résolu."); load(); };
  const suspendProfile = async (id) => { await api.post(`/academie-kaizen/community/${id}/suspend`); toast.success("Profil suspendu."); load(); };

  const exportCsv = (rows, cols, filename) => {
    const esc = (v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`;
    const header = cols.map((c) => esc(c.label)).join(",");
    const body = rows.map((r) => cols.map((c) => esc(r[c.key])).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + header + "\n" + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} ligne(s) exportée(s).`);
  };

  const relanceCarnets = async () => {
    if (!window.confirm("Envoyer un courriel de ré-engagement à tous les élèves dont le carnet est inactif depuis plus de 30 jours ?")) return;
    try {
      toast.loading("Envoi des relances…", { id: "rel" });
      const r = await api.post("/academie-kaizen/carnets/relance-inactifs");
      toast.success(`${r.data.sent} relance(s) envoyée(s). ${r.data.skipped_recent} ignorée(s) (déjà relancés récemment).`, { id: "rel", duration: 7000 });
    } catch { toast.error("Échec de l'envoi.", { id: "rel" }); }
  };

  if (!data) return <div className="p-8 text-white/50">Chargement…</div>;
  const k = data.kpi;
  const flt = (rows, keys) => rows.filter((r) => !q || keys.some((key) => (r[key] || "").toString().toLowerCase().includes(q.toLowerCase())));

  const TABS = [
    { id: "overview", label: "Vue d'ensemble", icon: ChartBar },
    { id: "members", label: "Membres", icon: Users },
    { id: "payments", label: "Paiements & Abonnements", icon: CreditCard },
    { id: "carnets", label: "Carnets", icon: Notebook },
    { id: "suivis", label: "Suivis", icon: ClipboardText },
    { id: "community", label: "Cercle K'Ien", icon: UsersThree },
  ];

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto" data-testid="academie-kaizen">
      <div className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-black text-white">🧭 Centre de gestion — Académie Kaizen</h1>
        <p className="text-white/50 text-sm mt-1">Membres, paiements, carnets, suivis et communauté — tout au même endroit.</p>
      </div>

      <div className="flex gap-2 flex-wrap mb-6 border-b border-white/10 pb-3">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 ${tab === t.id ? "bg-[#D90429] text-white" : "text-white/60 hover:bg-white/5"}`}>
            <t.icon size={16} /> {t.label}
            {t.id === "suivis" && k.open_suivis > 0 && <span className="bg-amber-500 text-black text-[10px] px-1.5 rounded-full">{k.open_suivis}</span>}
            {t.id === "payments" && k.duplicate_payments > 0 && <span className="bg-red-600 text-white text-[10px] px-1.5 rounded-full">{k.duplicate_payments}</span>}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="overview-grid">
          <Kpi icon={Users} label="Membres actifs" value={k.active_members} tone="green" />
          <Kpi icon={UsersThree} label="Essais en cours" value={k.trials} tone="gold" />
          <Kpi icon={Plus} label="Nouveaux 7j" value={k.new_7d} tone="blue" />
          <Kpi icon={CreditCard} label="Abonnements actifs" value={k.active_subs} tone="green" />
          <Kpi icon={Warning} label="Paiements en retard" value={k.past_due} tone="amber" />
          <Kpi icon={Notebook} label="Carnets inactifs 30j" value={k.inactive_carnets} tone="amber" />
          <Kpi icon={Warning} label="Doublons paiement" value={k.duplicate_payments} tone="red" />
          <Kpi icon={ClipboardText} label="Suivis ouverts" value={k.open_suivis} tone="gold" />
        </div>
      )}

      {(tab === "members" || tab === "payments" || tab === "carnets") && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-3 text-white/30" />
            <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="w-full sm:w-80 bg-black/30 border border-white/15 rounded-md pl-9 pr-3 py-2 text-sm text-white outline-none focus:border-[#C9A961]" />
          </div>
          {tab === "members" && (
            <button data-testid="export-members" onClick={() => exportCsv(data.members, [{ key: "name", label: "Nom" }, { key: "email", label: "Courriel" }, { key: "phone", label: "Téléphone" }, { key: "discipline", label: "Discipline" }, { key: "belt", label: "Niveau" }, { key: "statut", label: "Statut" }, { key: "tarif_mensuel", label: "Tarif mensuel" }], "membres-kaizen.csv")} className="px-4 py-2 rounded-md border border-emerald-700 bg-emerald-950/30 hover:bg-emerald-950/50 text-emerald-200 text-sm flex items-center gap-2"><DownloadSimple size={16} /> Exporter CSV</button>
          )}
          {tab === "payments" && (
            <button data-testid="export-payments" onClick={() => exportCsv(data.payments, [{ key: "name", label: "Nom" }, { key: "email", label: "Courriel" }, { key: "tarif_mensuel", label: "Tarif mensuel" }, { key: "stripe_status", label: "Statut Stripe" }, { key: "prochain_paiement", label: "Prochain paiement" }, { key: "stripe_customer_id", label: "Client Stripe" }], "paiements-kaizen.csv")} className="px-4 py-2 rounded-md border border-emerald-700 bg-emerald-950/30 hover:bg-emerald-950/50 text-emerald-200 text-sm flex items-center gap-2"><DownloadSimple size={16} /> Exporter CSV</button>
          )}
          {tab === "carnets" && (
            <button data-testid="relance-carnets" onClick={relanceCarnets} className="px-4 py-2 rounded-md border border-[#C9A961]/60 bg-[#C9A961]/10 hover:bg-[#C9A961]/20 text-[#C9A961] text-sm flex items-center gap-2"><PaperPlaneTilt size={16} /> Relancer les carnets inactifs</button>
          )}
        </div>
      )}

      {tab === "members" && (
        <div className="overflow-x-auto border border-white/10 rounded-lg" data-testid="members-table">
          <table className="w-full text-sm text-left">
            <thead className="bg-white/5 text-white/50 text-xs uppercase"><tr>
              <th className="px-4 py-3">Nom</th><th className="px-4 py-3">Courriel</th><th className="px-4 py-3">Discipline</th><th className="px-4 py-3">Niveau</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3">Tarif</th>
            </tr></thead>
            <tbody>
              {flt(data.members, ["name", "email", "discipline"]).map((m) => (
                <tr key={m.id} className="border-t border-white/5 text-white/80" data-testid={`member-row-${m.id}`}>
                  <td className="px-4 py-2.5 font-medium text-white">{m.name}{m.en_retard && <span className="ml-2 text-[10px] bg-amber-900/40 text-amber-300 px-1.5 rounded">retard</span>}</td>
                  <td className="px-4 py-2.5 text-white/50">{m.email}</td>
                  <td className="px-4 py-2.5">{m.discipline || "—"}</td>
                  <td className="px-4 py-2.5">{m.belt || "—"}</td>
                  <td className="px-4 py-2.5">{m.statut || "—"}</td>
                  <td className="px-4 py-2.5">{m.tarif_mensuel ? `${m.tarif_mensuel} $` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "payments" && (
        <>
          {dups.filter((d) => !d.resolved).length > 0 && (
            <div className="mb-4 border border-red-700/50 bg-red-950/20 rounded-lg p-4" data-testid="duplicate-alerts">
              <p className="text-red-300 font-bold text-sm mb-2 flex items-center gap-2"><Warning size={16} /> Doublons de paiement détectés</p>
              {dups.filter((d) => !d.resolved).map((d) => (
                <div key={d.id} className="flex items-center justify-between text-sm text-white/80 py-1" data-testid={`dup-${d.id}`}>
                  <span><b>{d.name}</b> ({d.email}) — {d.count_24h}× en &lt;24h · {d.plan_label} {d.amount_cad ? `${d.amount_cad}$` : ""}</span>
                  <button onClick={() => resolveDup(d.id)} data-testid={`dup-resolve-${d.id}`} className="text-xs px-2 py-1 rounded border border-white/20 text-white/70 hover:bg-white/5">Marquer résolu</button>
                </div>
              ))}
            </div>
          )}
          <div className="overflow-x-auto border border-white/10 rounded-lg" data-testid="payments-table">
            <table className="w-full text-sm text-left">
              <thead className="bg-white/5 text-white/50 text-xs uppercase"><tr>
                <th className="px-4 py-3">Nom</th><th className="px-4 py-3">Forfait</th><th className="px-4 py-3">Statut Stripe</th><th className="px-4 py-3">Prochain paiement</th><th className="px-4 py-3">Stripe</th>
              </tr></thead>
              <tbody>
                {flt(data.payments, ["name", "email"]).map((m) => (
                  <tr key={m.id} className="border-t border-white/5 text-white/80" data-testid={`payment-row-${m.id}`}>
                    <td className="px-4 py-2.5 font-medium text-white">{m.name}</td>
                    <td className="px-4 py-2.5">{m.tarif_mensuel ? `${m.tarif_mensuel} $/mois` : "—"}</td>
                    <td className="px-4 py-2.5">{m.stripe_status || m.statut || "—"}</td>
                    <td className="px-4 py-2.5">{m.prochain_paiement || "—"}</td>
                    <td className="px-4 py-2.5">{m.stripe_customer_id ? <a href={`https://dashboard.stripe.com/customers/${m.stripe_customer_id}`} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">Ouvrir ↗</a> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "carnets" && (
        <div className="overflow-x-auto border border-white/10 rounded-lg" data-testid="carnets-table">
          <table className="w-full text-sm text-left">
            <thead className="bg-white/5 text-white/50 text-xs uppercase"><tr>
              <th className="px-4 py-3">Élève</th><th className="px-4 py-3">Discipline</th><th className="px-4 py-3">Ceinture</th><th className="px-4 py-3">Objectif</th><th className="px-4 py-3">Dernière activité</th>
            </tr></thead>
            <tbody>
              {flt(data.carnets, ["student_name", "discipline"]).map((c) => (
                <tr key={c.id} className="border-t border-white/5 text-white/80" data-testid={`carnet-row-${c.id}`}>
                  <td className="px-4 py-2.5 font-medium text-white">{c.student_name}{c.a_relancer && <span className="ml-2 text-[10px] bg-amber-900/40 text-amber-300 px-1.5 rounded">à relancer</span>}</td>
                  <td className="px-4 py-2.5">{c.discipline || "—"}</td>
                  <td className="px-4 py-2.5">{c.current_belt || "—"}</td>
                  <td className="px-4 py-2.5">{c.target_belt || "—"}</td>
                  <td className="px-4 py-2.5 text-white/50">{c.updated_at ? new Date(c.updated_at).toLocaleDateString("fr-CA") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "suivis" && (
        <div data-testid="suivis-panel">
          <div className="border border-white/10 bg-white/[0.02] rounded-lg p-4 mb-5 grid sm:grid-cols-4 gap-3">
            <select data-testid="suivi-type" value={newSuivi.type} onChange={(e) => setNewSuivi({ ...newSuivi, type: e.target.value })} className="bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white">
              <option value="relance">Relance essai</option><option value="bienvenue">Message bienvenue</option><option value="paiement">Paiement à corriger</option><option value="inactif">Membre inactif</option><option value="support">Demande de soutien</option><option value="note">Note interne</option>
            </select>
            <input data-testid="suivi-member" value={newSuivi.member_name} onChange={(e) => setNewSuivi({ ...newSuivi, member_name: e.target.value })} placeholder="Membre (optionnel)" className="bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white" />
            <input data-testid="suivi-note" value={newSuivi.note} onChange={(e) => setNewSuivi({ ...newSuivi, note: e.target.value })} placeholder="Note / action" className="bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm text-white" />
            <div className="flex gap-2">
              <input type="date" data-testid="suivi-due" value={newSuivi.due_date} onChange={(e) => setNewSuivi({ ...newSuivi, due_date: e.target.value })} className="bg-black/30 border border-white/15 rounded-md px-2 py-2 text-sm text-white flex-1" />
              <button onClick={addSuivi} data-testid="suivi-add" className="px-3 py-2 rounded-md bg-[#C9A961] text-black font-bold"><Plus size={16} /></button>
            </div>
          </div>
          <div className="space-y-2">
            {suivis.length === 0 && <p className="text-white/40 text-sm text-center py-8">Aucun suivi. Crée ta première tâche ci-dessus.</p>}
            {suivis.map((s) => (
              <div key={s.id} className={`flex items-center justify-between border border-white/10 rounded-md px-4 py-3 ${s.done ? "opacity-40" : ""}`} data-testid={`suivi-${s.id}`}>
                <div>
                  <span className="text-[10px] uppercase bg-white/10 text-white/60 px-2 py-0.5 rounded mr-2">{s.type}</span>
                  <span className="text-white text-sm font-medium">{s.member_name || "—"}</span>
                  <span className="text-white/60 text-sm ml-2">{s.note}</span>
                  {s.due_date && <span className="text-white/40 text-xs ml-2">· échéance {s.due_date}</span>}
                </div>
                <div className="flex gap-2">
                  {!s.done && <button onClick={() => doneSuivi(s.id)} data-testid={`suivi-done-${s.id}`} className="text-emerald-400 hover:text-emerald-300"><CheckCircle size={20} /></button>}
                  <button onClick={() => delSuivi(s.id)} data-testid={`suivi-del-${s.id}`} className="text-red-400/70 hover:text-red-400"><Trash size={18} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "community" && community && (
        <div data-testid="community-panel">
          {cercleStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5" data-testid="cercle-stats">
              <Kpi icon={UsersThree} label="Membres actifs" value={cercleStats.active_members} tone="green" />
              <Kpi icon={ChartBar} label="Messages ce mois" value={cercleStats.messages_month} tone="blue" />
              <Kpi icon={UsersThree} label="Rencontres ce mois" value={cercleStats.meetups_month} tone="gold" />
              <Kpi icon={CheckCircle} label="Contacts acceptés" value={cercleStats.accepted_contacts} tone="green" />
            </div>
          )}
          <div className="grid grid-cols-3 gap-4 mb-3">
            <Kpi icon={UsersThree} label="Profils visibles" value={community.visible_count} tone="green" />
            <Kpi icon={Users} label="Total profils" value={community.total} tone="gold" />
            <Kpi icon={Warning} label="Signalés" value={community.reported_count} tone="red" />
          </div>
          <p className="text-white/40 text-xs mb-5" data-testid="community-stats">🏘️ {community.groups_count} groupes · 📅 {community.meetups_count} rencontres · ✉️ {community.pending_requests} demande(s) de contact en attente</p>
          {modLog.length > 0 && (
            <div className="border border-white/10 bg-white/[0.02] rounded-lg p-4 mb-5" data-testid="moderation-log">
              <p className="text-white/60 text-xs uppercase tracking-wider font-bold mb-2">📋 Journal de modération</p>
              <div className="space-y-1 max-h-56 overflow-y-auto">
                {modLog.map((l) => (
                  <div key={l.id} className="text-xs text-white/60 flex items-center gap-2" data-testid={`modlog-${l.id}`}>
                    <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${l.action === "suspend" ? "bg-red-900/40 text-red-300" : "bg-amber-900/40 text-amber-300"}`}>{l.action === "suspend" ? "suspension" : "signalement"}</span>
                    <span className="text-white/80">{l.display_name}</span>
                    <span className="text-white/40">{l.detail}</span>
                    <span className="text-white/25 ml-auto">{new Date(l.created_at).toLocaleString("fr-CA")} · {l.actor}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(community.reports || []).length > 0 && (
            <div className="border border-red-800/50 bg-red-950/20 rounded-lg p-4 mb-5" data-testid="community-reports">
              <p className="text-red-300 font-bold text-sm mb-2 flex items-center gap-2"><Warning size={16} /> Signalements ({community.reports.length})</p>
              {community.reports.slice(0, 10).map((r) => (
                <div key={r.id} className="flex items-center justify-between text-white/70 text-xs py-1" data-testid={`report-${r.id}`}>
                  <span><b className="text-white/90">{r.display_name}</b> — {r.reason || "aucune raison fournie"} <span className="text-white/30">({new Date(r.created_at).toLocaleDateString("fr-CA")})</span></span>
                  {r.profile_visible && <button onClick={() => suspendProfile(r.profile_id)} data-testid={`report-suspend-${r.id}`} className="text-[11px] px-2 py-1 rounded border border-red-700 text-red-300 hover:bg-red-950/40 shrink-0 ml-3">Suspendre</button>}
                </div>
              ))}
            </div>
          )}
          {community.profiles.length === 0 ? (
            <p className="text-white/40 text-sm text-center py-8">Aucun membre n'a encore rejoint le Cercle K'Ien.</p>
          ) : (
            <div className="overflow-x-auto border border-white/10 rounded-lg">
              <table className="w-full text-sm text-left">
                <thead className="bg-white/5 text-white/50 text-xs uppercase"><tr>
                  <th className="px-4 py-3">Pseudo</th><th className="px-4 py-3">Région</th><th className="px-4 py-3">Disciplines</th><th className="px-4 py-3">Visible</th><th className="px-4 py-3"></th>
                </tr></thead>
                <tbody>
                  {community.profiles.map((p) => (
                    <tr key={p.id} className="border-t border-white/5 text-white/80" data-testid={`mod-profile-${p.id}`}>
                      <td className="px-4 py-2.5 font-medium text-white">{p.display_name}</td>
                      <td className="px-4 py-2.5">{[p.region, p.province_pays].filter(Boolean).join(" · ") || "—"}</td>
                      <td className="px-4 py-2.5">{(p.disciplines || []).map((d) => DISC_LABEL[d] || d).join(", ") || "—"}</td>
                      <td className="px-4 py-2.5">{p.visible ? <span className="text-emerald-400">Oui</span> : <span className="text-white/40">Non</span>}</td>
                      <td className="px-4 py-2.5">{p.visible && <button onClick={() => suspendProfile(p.id)} data-testid={`mod-suspend-${p.id}`} className="text-xs px-2 py-1 rounded border border-red-800 text-red-300 hover:bg-red-950/40">Suspendre</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
